import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useApi } from '@/contexts/AuthContext';
import { Check, Compass, ChevronDown, ChevronUp } from 'lucide-react';

const STEPS = [
  { id: 'dashboard', title: 'Explore your dashboard', desc: 'Hero KPIs show GWP, active policies, open claims and loss ratio at a glance.', path: '/today' },
  { id: 'search', title: 'Search for a business', desc: 'Find any AU/NZ entity by name or ABN — try the example chips.', path: '/new-business?tab=submit' },
  { id: 'classes', title: 'Start a class submission', desc: 'Pick an insurance class and see exactly how it is underwritten.', path: '/new-business?tab=quote' },
  { id: 'risk', title: 'Complete the risk details', desc: 'Turnover, sum insured and claims history drive the rating.', path: '/new-business?tab=quote' },
  { id: 'score', title: 'Review the AIRA score', desc: 'Submit for scoring — 21 dimensions, decision in seconds.', path: '/new-business?tab=quote' },
  { id: 'bind', title: 'Bind the policy', desc: 'Approved quotes bind instantly with the full premium waterfall.', path: '/new-business?tab=pipeline' },
  { id: 'documents', title: 'Download documents', desc: 'Policy schedule, Certificate of Currency and closing note.', path: '/policies' },
];

const PROGRESS_KEY = 'fraime_guided_progress';
const DONE_KEY = 'fraime_first_run_done';

function loadProgress() {
  try { return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}'); } catch { return {}; }
}

export default function GuidedPath() {
  const { user } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => localStorage.getItem(DONE_KEY) === 'true');
  const [minimized, setMinimized] = useState(() => localStorage.getItem('fraime_guided_min') === 'true');
  const [progress, setProgress] = useState(loadProgress);

  const setMin = (v) => { setMinimized(v); localStorage.setItem('fraime_guided_min', String(v)); };

  if (!user || ['broker', 'regulator', 'investor'].includes(user.role) || user.first_run_completed !== false || hidden) return null;

  const doneCount = STEPS.filter(s => progress[s.id]).length;

  const markStep = (step) => {
    const next = { ...progress, [step.id]: true };
    setProgress(next);
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(next));
    navigate(step.path);
  };

  const finish = async () => {
    setHidden(true);
    localStorage.setItem(DONE_KEY, 'true');
    try { await api.post('/auth/first-run-complete'); } catch { /* persisted locally regardless */ }
  };

  if (minimized) {
    return (
      <button onClick={() => setMin(false)} data-testid="guided-path-toggle"
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-xl text-xs font-semibold text-white transition-transform hover:scale-105"
        style={{ background: '#1A4C7C' }}>
        <Compass className="w-4 h-4" /> Guided path · {doneCount}/{STEPS.length}
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[340px] rounded-md shadow-2xl overflow-hidden bg-white"
      style={{ border: '1px solid rgba(26,76,124,0.25)' }} data-testid="guided-path-panel">
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#0D1B2A' }}>
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4" style={{ color: '#FF9933' }} />
          <div>
            <p className="text-xs font-bold text-white">Welcome to frAIme — the golden path</p>
            <p className="text-[10px]" style={{ color: '#B0BAC5' }}>7 steps from entity search to bound policy</p>
          </div>
        </div>
        <button onClick={() => setMin(true)} className="p-1 rounded hover:bg-white/10" data-testid="guided-path-minimize" aria-label="Minimize guided path">
          <ChevronDown className="w-4 h-4 text-white" />
        </button>
      </div>

      <div className="h-1" style={{ background: 'rgba(136,150,168,0.15)' }}>
        <div className="h-full transition-all duration-300" style={{ width: `${(doneCount / STEPS.length) * 100}%`, background: '#FF9933' }} />
      </div>

      <div className="max-h-[320px] overflow-y-auto py-1">
        {STEPS.map((s, i) => {
          const isDone = !!progress[s.id];
          return (
            <button key={s.id} onClick={() => markStep(s)} data-testid={`guided-path-step-${s.id}`}
              className="w-full px-4 py-2.5 flex items-start gap-3 text-left transition-colors hover:bg-[#F5F7FA]">
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5"
                style={{ background: isDone ? '#1D9E75' : 'rgba(26,76,124,0.1)', color: isDone ? '#fff' : '#1A4C7C' }}>
                {isDone ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              <span>
                <span className="block text-xs font-semibold" style={{ color: isDone ? '#1D9E75' : '#0D1B2A' }}>{s.title}</span>
                <span className="block text-[10px] leading-snug mt-0.5" style={{ color: '#5C6B7A' }}>{s.desc}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="px-4 py-3 flex items-center justify-between border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        <button onClick={finish} className="text-[11px] hover:underline" style={{ color: '#5C6B7A' }} data-testid="guided-path-skip">
          Skip tour
        </button>
        <button onClick={finish} data-testid="guided-path-finish"
          className="px-4 py-1.5 rounded-sm text-xs font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: doneCount === STEPS.length ? '#1D9E75' : '#1A4C7C' }}>
          {doneCount === STEPS.length ? 'Finish — all done ✓' : `Mark complete (${doneCount}/${STEPS.length})`}
        </button>
      </div>
    </div>
  );
}
